/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Effect hooks for saved views (auto-apply, URL sync)
 */

"use client";

import { logger } from "@/lib/utils/logger";
import type { NormalizedSavedView } from "@/types/saved-views";
import { useEffect } from "react";

interface UseSavedViewsEffectsOptions<TFilters> {
  autoApplyDefault: boolean;
  isLoading: boolean;
  allViews: NormalizedSavedView<TFilters>[];
  selectedViewId: string | undefined;
  selectedViewIdValue: string;
  enabled: boolean;
  isLoaded: boolean;
  isSignedIn: boolean;
  syncWithUrl: boolean;
  applyingViewRef: React.MutableRefObject<boolean>;
  lastAppliedViewIdRef: React.MutableRefObject<string | null>;
  currentFiltersRef: React.MutableRefObject<TFilters | undefined>;
  onApplyView?: (filters: TFilters) => void;
  handleApplyView: (viewId: string | null) => void;
  currentFilters?: TFilters;
}

/**
 * Hook for saved views effects (auto-apply default view, URL sync)
 */
export function useSavedViewsEffects<TFilters extends Record<string, unknown>>({
  autoApplyDefault,
  isLoading,
  allViews,
  selectedViewId,
  selectedViewIdValue,
  enabled,
  isLoaded,
  isSignedIn,
  syncWithUrl,
  applyingViewRef,
  lastAppliedViewIdRef,
  currentFiltersRef,
  onApplyView,
  handleApplyView,
  currentFilters,
}: UseSavedViewsEffectsOptions<TFilters>) {
  // Update ref when currentFilters changes (but don't trigger effect)
  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters, currentFiltersRef]);

  // Auto-apply default view on mount
  useEffect(() => {
    if (!autoApplyDefault || isLoading || applyingViewRef.current) return;

    if (
      allViews.length > 0 &&
      !selectedViewIdValue &&
      enabled &&
      isLoaded &&
      isSignedIn
    ) {
      const defaultView = allViews.find((view) => view.is_default);
      if (defaultView) {
        handleApplyView(defaultView.id);
      }
    }
  }, [
    autoApplyDefault,
    isLoading,
    allViews,
    selectedViewIdValue,
    enabled,
    isLoaded,
    isSignedIn,
    handleApplyView,
    applyingViewRef,
  ]);

  // Auto-apply view when selectedViewId changes (from localStorage, not URL)
  // Only apply if viewId changed and we haven't already applied it
  useEffect(() => {
    if (!syncWithUrl || applyingViewRef.current || !selectedViewId) return;

    // Prevent re-applying the same view if we just applied it
    if (lastAppliedViewIdRef.current === selectedViewId) {
      return;
    }

    const view = allViews.find((v) => v.id === selectedViewId);
    if (!view || !onApplyView) return;

    // Check if filters are already applied (prevent unnecessary re-application)
    const currentFiltersSnapshot = currentFiltersRef.current;
    if (currentFiltersSnapshot) {
      const viewFilters = view.filters as TFilters;
      try {
        const filtersMatch =
          JSON.stringify(viewFilters) ===
          JSON.stringify(currentFiltersSnapshot);
        if (filtersMatch) {
          // Filters already match, mark as applied but don't re-apply
          lastAppliedViewIdRef.current = selectedViewId;
          return;
        }
      } catch (error) {
        logger.warn("Failed to compare filters, applying view anyway", {
          error,
        });
      }
    }

    // Apply the view
    applyingViewRef.current = true;
    lastAppliedViewIdRef.current = selectedViewId;
    try {
      const viewFilters = view.filters as TFilters;
      onApplyView(viewFilters);
    } finally {
      setTimeout(() => {
        applyingViewRef.current = false;
      }, 100);
    }
  }, [
    selectedViewId,
    allViews,
    onApplyView,
    syncWithUrl,
    applyingViewRef,
    lastAppliedViewIdRef,
    currentFiltersRef,
  ]);
}
